const PENDING_STATUSES = ["STARTED", "PENDING", "PROCESSING", "IN_PROGRESS"];
const FAILED_STATUSES = ["FAILED", "ERROR", "FAILURE"];
const SUCCESS_STATUSES = ["SUCCESS", "COMPLETED", "SUCCEEDED", "DONE"];

const INVALID_IMAGE_PATHS = new Set(["", "url", "=", "-", "null", "undefined"]);

const isValidImagePath = (path) => {
  if (path === null || path === undefined) {
    return false;
  }

  const value = String(path).trim();
  if (!value || INVALID_IMAGE_PATHS.has(value.toLowerCase())) {
    return false;
  }

  return value.length > 2;
};

export const getMatchImageUrl = (match) => {
  if (match?.output_url && isValidImagePath(match.output_url)) {
    return getInputImageUrl(match.output_url);
  }

  const imagePath = match?.image_path || "";
  if (!isValidImagePath(imagePath)) {
    return "";
  }

  return getInputImageUrl(imagePath);
};

export const getInputImageUrl = (inputUrl) => {
  if (!isValidImagePath(inputUrl)) {
    return "";
  }

  const value = String(inputUrl).trim();

  if (value.startsWith("http") || value.startsWith("blob:")) {
    return value;
  }

  if (value.startsWith("s3://")) {
    const key = value.replace(/^s3:\/\/[^/]+\//, "");
    return `https://pcimageupload.s3.ap-south-1.amazonaws.com/${key}`;
  }

  return `https://pcimageupload.s3.ap-south-1.amazonaws.com/${value}`;
};

const parseJsonField = (value) => {
  if (!value) {
    return null;
  }
  if (typeof value === "object") {
    return value;
  }
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }
  return null;
};

const parseScoreValue = (score) => {
  if (score === null || score === undefined || score === "") {
    return 0;
  }
  const numeric = parseFloat(String(score).replace("%", ""));
  return Number.isNaN(numeric) ? 0 : numeric;
};

export const getUniqueMatchResults = (matches) => {
  if (!Array.isArray(matches) || !matches.length) {
    return [];
  }

  const uniqueByCriminalId = new Map();
  const withoutCriminalId = [];

  matches.forEach((match) => {
    const criminalId = match?.criminal_id;
    if (!criminalId) {
      withoutCriminalId.push(match);
      return;
    }

    const existing = uniqueByCriminalId.get(criminalId);
    if (
      !existing ||
      parseScoreValue(match.score) > parseScoreValue(existing.score)
    ) {
      uniqueByCriminalId.set(criminalId, match);
    }
  });

  return [...uniqueByCriminalId.values(), ...withoutCriminalId];
};

const getExternalResultItems = (resultResponse) => {
  if (!resultResponse) {
    return [];
  }

  const raw = resultResponse.data ?? resultResponse;

  if (Array.isArray(raw)) {
    return raw;
  }

  if (Array.isArray(raw?.data)) {
    return raw.data;
  }

  if (raw && typeof raw === "object") {
    return [raw];
  }

  return [];
};

const isTaskResultRow = (value) =>
  value &&
  typeof value === "object" &&
  (value.status ||
    value.result ||
    value.match ||
    value.data ||
    value.info ||
    value.task_id ||
    value.taks_id);

const unwrapApiResult = (apiResult) => {
  if (!apiResult) {
    return null;
  }

  if (Array.isArray(apiResult)) {
    return apiResult[0] || null;
  }

  if (apiResult.data !== undefined && apiResult.data !== null) {
    if (Array.isArray(apiResult.data)) {
      return apiResult.data[0] || null;
    }

    if (typeof apiResult.data === "object" && isTaskResultRow(apiResult.data)) {
      return apiResult.data;
    }
  }

  return apiResult;
};

const isMatchItem = (item) =>
  item &&
  typeof item === "object" &&
  (item.criminal_id || item.name || item.image_path || item.output_url);

const extractMatchesFromObject = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    if (value.length && isMatchItem(value[0])) {
      return value;
    }

    return value.flatMap((item) => extractMatchesFromObject(item));
  }

  if (typeof value !== "object") {
    const parsed = parseJsonField(value);
    return parsed ? extractMatchesFromObject(parsed) : [];
  }

  if (Array.isArray(value.match)) {
    return value.match;
  }

  if (Array.isArray(value.data) && value.data.length && isMatchItem(value.data[0])) {
    return value.data;
  }

  const parsedData = parseJsonField(value.data);
  if (Array.isArray(parsedData) && parsedData.length && isMatchItem(parsedData[0])) {
    return parsedData;
  }

  const parsedResult = parseJsonField(value.result);
  if (parsedResult) {
    const matches = extractMatchesFromObject(parsedResult);
    if (matches.length) {
      return matches;
    }
  }

  if (value.result && typeof value.result === "object") {
    const matches = extractMatchesFromObject(value.result);
    if (matches.length) {
      return matches;
    }
  }

  return [];
};

const extractMatches = (apiResult) => {
  const unwrapped = unwrapApiResult(apiResult);
  let matches = extractMatchesFromObject(unwrapped);

  if (!matches.length) {
    matches = extractMatchesFromObject(apiResult);
  }

  if (!matches.length) {
    matches = getPollMatchResults(apiResult);
  }

  return matches;
};

const parseNestedJsonField = (value) => {
  let parsed = parseJsonField(value);
  if (typeof parsed === "string") {
    parsed = parseJsonField(parsed);
  }
  return parsed;
};

const isFailedStatus = (status) =>
  FAILED_STATUSES.includes(String(status || "").toUpperCase());

const extractStatusMessage = (apiResult) => {
  const row = unwrapApiResult(apiResult);
  if (!row) {
    return "";
  }

  const info = parseNestedJsonField(row.info) || {};
  const parsedResult = parseNestedJsonField(row.result) || row.result || {};

  return (
    row.nodata ||
    info.nodata ||
    (typeof parsedResult === "object" ? parsedResult.nodata : "") ||
    row.message ||
    info.message ||
    (typeof parsedResult === "object" ? parsedResult.message : "") ||
    ""
  );
};
const extractStatus = (apiResult) => {
  const row = unwrapApiResult(apiResult);
  if (!row) {
    return "";
  }

  const info = parseNestedJsonField(row.info) || {};
  const parsedResult = parseNestedJsonField(row.result) || row.result || {};

  return (
    row.status ||
    info.status ||
    (typeof parsedResult === "object" ? parsedResult.status : "") ||
    getExternalResultStatus(apiResult) ||
    ""
  );
};

export const getExternalResultStatus = (resultResponse) => {
  const items = getExternalResultItems(resultResponse);

  if (!items.length) {
    return "";
  }

  for (const item of items) {
    const status = String(item?.status || "").toUpperCase();
    if (SUCCESS_STATUSES.includes(status) || FAILED_STATUSES.includes(status)) {
      return status;
    }
  }

  return String(items[0]?.status || "").toUpperCase();
};

const getPollResultPayload = (item) =>
  parseNestedJsonField(item?.result) || item?.result || item;

const getPollMatchResults = (searchResult) => {
  const items = getExternalResultItems(searchResult);

  for (const item of items) {
    const result = getPollResultPayload(item);

    if (Array.isArray(result?.match)) {
      return result.match;
    }

    if (Array.isArray(item?.match)) {
      return item.match;
    }
  }

  const payload = items.find((item) =>
    SUCCESS_STATUSES.includes(String(item?.status || "").toUpperCase())
  ) || items[0] || {};
  const result = getPollResultPayload(payload);

  if (Array.isArray(result?.match)) {
    return result.match;
  }

  if (Array.isArray(payload?.match)) {
    return payload.match;
  }

  return [];
};

export const normalizeSearchResultForDisplay = (
  apiResult,
  inputImageUrlOverride = ""
) => {
  if (!apiResult) {
    return null;
  }

  const row = unwrapApiResult(apiResult);
  if (!row) {
    return null;
  }

  const info = parseNestedJsonField(row.info) || {};
  const parsedResult = parseNestedJsonField(row.result) || row.result || {};
  const matches = extractMatches(apiResult);
  const status = extractStatus(apiResult);
  const statusMessage = extractStatusMessage(apiResult);

  const inputUrl =
    inputImageUrlOverride ||
    row.input_url ||
    row.image_path ||
    info.input_url ||
    (typeof parsedResult === "object" ? parsedResult.input_url : "") ||
    (typeof parsedResult === "object" ? parsedResult.image_path : "") ||
    "";

  return {
    status,
    statusMessage,
    isFailed: isFailedStatus(status),
    inputImageUrl: getInputImageUrl(inputUrl),
    matchResults: getUniqueMatchResults(matches),
  };
};

export const isPendingFaceSearchStatus = (status) =>
  !status || PENDING_STATUSES.includes(status);

export const isFaceSearchComplete = (resultResponse) => {
  const status = getExternalResultStatus(resultResponse);

  if (SUCCESS_STATUSES.includes(status)) {
    return true;
  }

  if (extractMatches(resultResponse).length > 0) {
    return true;
  }

  return !isPendingFaceSearchStatus(status) && Boolean(status);
};

export { FAILED_STATUSES, SUCCESS_STATUSES, isFailedStatus };
